<template>
  <PageHeader>
    <template #header-title>
      {{ $t('Recruiters') }}
    </template>
    <template v-if="!recruitersLoadError" #header-actions>
      <AddCompanyRecruiter
        :company-recruiter-ids="companyRecruiterIds"
        :manage-workers="manageWorkers"
        :can-configure-recruiter-manages-workers="canConfigureRecruiterManagesWorkers"
        @added="refetch"
      />
    </template>
  </PageHeader>
  <div v-if="recruitersLoadError" class="tw-mt-9">
    <ContentLoadingErrorNotice />
  </div>
  <div v-else>
    <CompanyRecruiterContacts
      :entries="entries"
      :loading="loading"
      :manage-workers="manageWorkers"
      :can-configure-recruiter-manages-workers="canConfigureRecruiterManagesWorkers"
      :show-recruiter-fee="showRecruiterFee"
      @updated="refetch"
      @row-click="openDetails"
    />
    <WdsDrawer v-model:show="detailsSidebarVisible" position="right" width="420px" padding="0">
      <RecruiterDetailPanel
        v-if="selectedCompanyRecruiterId"
        :company-recruiter-id="selectedCompanyRecruiterId"
        :tab-key="selectedTab ?? undefined"
        @close="closeDetails"
        @updated="refetch"
        @deleted="onDeleted"
      />
    </WdsDrawer>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsDrawer } from '@worksome/wds-components'
import SilentNetworkError from '@/utils/notification/SilentNetworkError'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'
import CompanyRecruiterContacts from '@/components/Contacts/CompanyRecruiter/CompanyRecruiterContacts.vue'
import AddCompanyRecruiter from '@/components/Contacts/CompanyRecruiter/AddCompanyRecruiter.vue'
import RecruiterDetailPanel from '@/components/RecruiterDetailPanel/RecruiterDetailPanel.vue'
import { CompanyRecruiters } from '@/components/Contacts/CompanyRecruiter/queries/CompanyRecruiters'
import { $t } from '@/composables/translation'
import PageHeader from '@/components/PageHeader.vue'
import useQueryEncoder from '@/composables/useQueryEncoder'

defineProps({
  manageWorkers: {
    type: Boolean,
    default: false,
  },
  canConfigureRecruiterManagesWorkers: {
    type: Boolean,
    default: false,
  },
  showRecruiterFee: {
    type: Boolean,
    default: false,
  },
})

const queryVariables = ref({
  accounts: [currentAccount.globalId],
  perPage: 50,
  page: 1,
})

const recruitersLoadError = ref(false)
const detailsSidebarVisible = ref(false)
const selectedCompanyRecruiterId = ref<string | null>(null)
const selectedTab = ref<string | null>(null)

const recruiterIdParam = useQueryEncoder('recruiter-id')
const activeTabParam = useQueryEncoder('tab')

const {
  result,
  loading,
  refetch,
  onError: onRecruitersLoadingError,
} = useQuery(CompanyRecruiters, queryVariables, {
  clientId: 'publicApi',
})

onRecruitersLoadingError((error) => {
  recruitersLoadError.value = true

  new SilentNetworkError({
    ex: error,
  }).dispatch()
})

const entries = computed(() =>
  // eslint-disable-next-line @typescript-eslint/no-shadow -- Baseline eslint ignore please fix if working on this
  result.value?.companyRecruiters.data.map((result) => ({
    id: result.recruiter.id,
    avatar: result.recruiter.avatar,
    name: result.recruiter.name,
    status: result.status?.toLowerCase(),
    email: result.email,
    fee: result.recruiterFee * 100,
    ownership: result.recruiterOwnershipDays,
    managesWorkers: result.recruiterManagesWorkers,
    hasHires: result.hasHires,
    pivotId: result.id,
    tags: result.tags?.map((appliedTag) => appliedTag.name),
  })),
)

const companyRecruiterIds = computed(
  () => result.value?.companyRecruiters.data.map((contact) => contact.id) ?? [],
)

const openDetails = (row: { pivotId: string }) => {
  detailsSidebarVisible.value = true
  selectedCompanyRecruiterId.value = row.pivotId
  selectedTab.value = null
  recruiterIdParam.value = row.pivotId
  activeTabParam.value = null
}

const closeDetails = () => {
  detailsSidebarVisible.value = false
  selectedCompanyRecruiterId.value = null
  selectedTab.value = null
  recruiterIdParam.value = null
  activeTabParam.value = null
}

const onDeleted = () => {
  closeDetails()
  refetch()
}

onMounted(() => {
  if (recruiterIdParam.value) {
    detailsSidebarVisible.value = true
    selectedCompanyRecruiterId.value = recruiterIdParam.value
    selectedTab.value = activeTabParam.value
  }
})
</script>
